import { Badge, Box, Button, HStack, Icon, Text } from '@chakra-ui/react'
import { FiFilter } from 'react-icons/fi'
import type { AddressMode, WalkType } from '../../../types/canvasser'
import type { StatusFilter } from '../../../lib/walkStops'
import type { WalkSurface } from '../../../lib/walkPrefs'

const KIND: Record<AddressMode, string> = { Home: 'Household', Work: 'Worksites', Hybrid: 'Hybrid' }
const FILTER_LABEL: Record<StatusFilter, string> = { remaining: 'Remaining', revisit: 'Revisit', all: 'All' }

/** "Household · walk" | "Worksites · drive" | "Hybrid · drive" */
export function listKindLabel(mode: AddressMode, walkType: WalkType): string {
  return `${KIND[mode]} · ${walkType.toLowerCase()}`
}

/** [Spec §4.2] The walk screen's top strip: list kind, progress, List/Map switch, filter chip. */
export function WalkStrip({ addressMode, walkType, progress, peopleCount, surface, onSurfaceChange, filter, onOpenFilters }: {
  addressMode: AddressMode; walkType: WalkType
  progress: { done: number; total: number; revisit: number }
  /** Set in the Contacts view: the list shows people, so say how many next to the door count. */
  peopleCount?: number
  surface: WalkSurface; onSurfaceChange: (s: WalkSurface) => void
  filter: StatusFilter; onOpenFilters: () => void
}) {
  const kind = listKindLabel(addressMode, walkType)
  const unit = addressMode === 'Home' ? 'doors' : 'stops'
  const progressText = `${progress.done} of ${progress.total} ${unit}`
    + (peopleCount != null ? ` · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` : '')
    + (progress.revisit > 0 ? ` · ${progress.revisit} to revisit` : '')
  const surfaceButton = (s: WalkSurface, label: string) => (
    <Button size="sm" minH="44px" aria-pressed={surface === s} onClick={() => onSurfaceChange(s)}
      variant={surface === s ? 'solid' : 'ghost'}
      bg={surface === s ? 'accent' : undefined} color={surface === s ? 'accent.fg' : 'fg'}>
      {label}
    </Button>
  )
  return (
    <Box bg="surface" borderBottomWidth="1px" borderColor="border" px={3} py={2}>
      <HStack justify="space-between" gap={2} wrap="wrap">
        <Box minW={0}>
          <Badge aria-label={`List type: ${kind}`} bg="accent.soft" color="accent">{kind}</Badge>
          <Text fontSize="sm" color="fg.muted" mt={1}>{progressText}</Text>
        </Box>
        <HStack gap={2}>
          <HStack gap={0} borderWidth="1px" borderColor="border" borderRadius="md" p="2px">
            {surfaceButton('list', 'List')}
            {surfaceButton('map', 'Map')}
          </HStack>
          <Button size="sm" minH="44px" variant="outline" borderColor="border" onClick={onOpenFilters}>
            <Icon as={FiFilter} aria-hidden />
            {`Filters · ${FILTER_LABEL[filter]}`}
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
}
